import * as v from "valibot";
import { CompaniesSchema, CompanySchema, StateSchema } from "../validate.ts";
import type { AppState, Company } from "./types.ts";

const TOKEN_KEY = "likvid_token";

function readToken(): string | null {
  try {
    return sessionStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

function clearToken(): void {
  try {
    sessionStorage.removeItem(TOKEN_KEY);
  } catch {
    /* ignore */
  }
}

function promptForToken(message: string): string | null {
  const entered = typeof prompt === "function" ? prompt(message) : null;
  if (!entered) return null;
  try {
    sessionStorage.setItem(TOKEN_KEY, entered);
  } catch {
    /* ignore */
  }
  return entered;
}

function authHeaders(token: string | null): Record<string, string> {
  return token ? { authorization: `Bearer ${token}` } : {};
}

async function authedFetch(
  input: string,
  init: RequestInit = {},
): Promise<Response> {
  const baseHeaders = (init.headers as Record<string, string>) ?? {};
  let token = readToken();
  let res = await fetch(input, {
    ...init,
    headers: { ...baseHeaders, ...authHeaders(token) },
  });
  if (res.status !== 401) return res;

  // First 401: token may be missing or wrong. Prompt and retry once.
  clearToken();
  token = promptForToken("Token för Likvid:");
  if (!token) return res;
  res = await fetch(input, {
    ...init,
    headers: { ...baseHeaders, ...authHeaders(token) },
  });
  if (res.status === 401) clearToken();
  return res;
}

function parseOrThrow<S extends v.GenericSchema>(
  schema: S,
  raw: unknown,
  label: string,
): v.InferOutput<S> {
  const parsed = v.safeParse(schema, raw);
  if (!parsed.success) {
    const issue = parsed.issues[0];
    const path = issue?.path?.map((p) => p.key).join(".") ?? "root";
    throw new Error(`${label}: invalid server payload at ${path}`);
  }
  return parsed.output;
}

const companyPath = (companyId: string) =>
  `/api/companies/${encodeURIComponent(companyId)}`;

export async function listCompanies(): Promise<Company[]> {
  const res = await authedFetch("/api/companies");
  if (!res.ok) throw new Error(`listCompanies failed: HTTP ${res.status}`);
  return parseOrThrow(CompaniesSchema, await res.json(), "listCompanies");
}

export class NameTakenError extends Error {
  constructor() {
    super("company name taken");
  }
}

async function sendCompanyName(
  path: string,
  method: "POST" | "PATCH",
  name: string,
): Promise<Company> {
  const res = await authedFetch(path, {
    method,
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ name }),
  });
  if (res.status === 409) throw new NameTakenError();
  if (!res.ok) throw new Error(`${method} ${path} failed: HTTP ${res.status}`);
  return parseOrThrow(CompanySchema, await res.json(), path);
}

export function createCompany(name: string): Promise<Company> {
  return sendCompanyName("/api/companies", "POST", name);
}

export function renameCompany(id: string, name: string): Promise<Company> {
  return sendCompanyName(companyPath(id), "PATCH", name);
}

export async function loadState(companyId: string): Promise<AppState> {
  const res = await authedFetch(`${companyPath(companyId)}/state`);
  if (!res.ok) {
    throw new Error(`loadState failed: HTTP ${res.status}`);
  }
  return parseOrThrow(StateSchema, await res.json(), "loadState");
}

export type SaveResult =
  | { ok: true; version: number }
  | { ok: false; status: "conflict" | "error" };

export async function saveState(
  companyId: string,
  state: AppState,
): Promise<SaveResult> {
  try {
    const res = await authedFetch(`${companyPath(companyId)}/state`, {
      method: "PUT",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({
        version: state.version,
        startBalance: state.startBalance,
        rows: state.rows,
        fixedCosts: state.fixedCosts,
        costCenters: state.costCenters,
      }),
    });
    if (res.ok) {
      const data = (await res.json()) as { version: number };
      return { ok: true, version: data.version };
    }
    if (res.status === 409) return { ok: false, status: "conflict" };
    return { ok: false, status: "error" };
  } catch {
    return { ok: false, status: "error" };
  }
}
